import { AnimatePresence, motion, type Variants } from 'motion/react';
import { Button } from '../design-system/Button';
import type { IframePoolOrderItem } from '../api';
import { SkipBolt } from './SkipBolt';
import type { SellState } from './WinnerStep';
import { FmvOnly } from './FmvOnly';
import { RevealStage } from './RevealStage';
import { RollingNumber } from './RollingNumber';
import { BuybackReceivedLine } from './BuybackReceivedLine';
import { BuybackRippleWaves } from './BuybackRippleWaves';
import { currencyAffix } from '../currency';

export interface RevealBottomPanelProps {
  item: IframePoolOrderItem;
  offer: NonNullable<IframePoolOrderItem['bestActiveOffer']>;
  sellState: SellState;
  /** Accept the buyback — fires the sell immediately (no confirmation
   *  modal). The screen stays mounted so the celebration can play. */
  onAcceptBuyback: () => void;
  /** "Open Next Pack" — provisional wiring to the host's advance/close
   *  handler; final behaviour TBD. */
  onOpenNextPack: () => void;
  /** Forward-CTA label, computed by WinnerStep ("Buy another pack for
   *  {price}" or "Open Next Pack" — see WinnerContentProps). */
  nextPackCta: string;
  /** The forward CTA closes the reveal ("Done") instead of opening another
   *  pack — sleek skins it as a secondary button. Ignored here. */
  nextPackIsDone?: boolean;
  /** Sleek's Vault was tapped — the panel shows "Vaulted" itself; WinnerStep
   *  closes the reveal when it was the order's last pack. Ignored here. */
  onVault?: () => void;
  /** Sleek, the order's last pack: the reveal leaves by itself once the
   *  result has shown, so the resolved block has no CTA and calls this when
   *  it has risen in. Ignored here. */
  onResultShown?: () => void;
  /** Opens the Create Shipping modal (its state lives in WinnerStep). */
  onCreateShipping: () => void;
  /** Whether the Create Shipping link is offered (item is on hands). */
  showCreateShipping: boolean;
  /** Reveal-cascade visibility flags for the entrance choreography. */
  visibleFmv: boolean;
  visibleCta: boolean;
  /** Create Shipping drops in a beat AFTER Accept Buyback (its own stage),
   *  so the bottom reads FMV → Accept Buyback → Create Shipping. */
  visibleShipping: boolean;
  /** Odometer-start flags, deferred past each row's entrance. */
  fmvRoll: boolean;
  ctaRoll: boolean;
  /** Returning view — paint final state with no entrance animation. */
  instant: boolean;
}

// ── Exit choreography (the "offer" phase leaving on accept) ──────────
// The container fans its children out in REVERSE DOM order
// (`staggerDirection: -1`) so they drop bottom-up:
//   Create Shipping ↓  →  Accept Buyback ↓  →  Fair Market Value ↓
const offerContainer: Variants = {
  exit: { transition: { staggerChildren: 0.12, staggerDirection: -1 } },
};
const offerChild: Variants = {
  // Entrance is owned by the inner RevealStage; this wrapper only
  // describes how each row flies away.
  exit: {
    y: 60,
    opacity: 0,
    transition: { duration: 0.34, ease: [0.4, 0, 1, 1] },
  },
};

// ── Entrance choreography (the "resolved" phase arriving on accept) ──
// Children fly UP from below in DOM order: the credited amount lands
// in the FMV slot first, then the Open Next Pack button.
const resolvedContainer: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.16, delayChildren: 0.06 } },
};
const resolvedChild: Variants = {
  hidden: { y: 60, opacity: 0 },
  show: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
  },
};

/**
 * Bottom of the winner screen. Two phases cross-faded by
 * `AnimatePresence` (mode="wait" — the offer fully leaves before the
 * payout arrives):
 *
 *   offer    → Fair Market Value + "Accept Buyback" + "Create Shipping"
 *   resolved → "+$X.XX" credited + "Open Next Pack"
 */
export function RevealBottomPanel({
  item,
  offer,
  sellState,
  onAcceptBuyback,
  onOpenNextPack,
  nextPackCta,
  onCreateShipping,
  showCreateShipping,
  visibleFmv,
  visibleCta,
  visibleShipping,
  fmvRoll,
  ctaRoll,
  instant,
}: RevealBottomPanelProps) {
  const { prefix, suffix } = currencyAffix();
  const hasFmv =
    item.fairMarketValue != null &&
    Number.isFinite(item.fairMarketValue) &&
    item.fairMarketValue > 0;

  return (
    <div className="w-full max-w-[480px] mx-auto shrink-0">
      <AnimatePresence mode="wait" initial={false}>
        {sellState !== 'sold' ? (
          <motion.div
            key="offer"
            variants={offerContainer}
            exit="exit"
            className="flex flex-col items-center gap-3 w-full"
          >
            {hasFmv && (
              <motion.div variants={offerChild}>
                <RevealStage visible={visibleFmv} instant={instant}>
                  <FmvOnly
                    fmv={item.fairMarketValue!}
                    roll={fmvRoll}
                    instant={instant}
                  />
                </RevealStage>
              </motion.div>
            )}

            <motion.div variants={offerChild} className="w-full">
              <RevealStage
                visible={visibleCta}
                instant={instant}
                className="w-full"
              >
                <Button
                  variant="primary"
                  size="xl"
                  onClick={onAcceptBuyback}
                  disabled={sellState === 'selling'}
                  loading={sellState === 'selling'}
                  fullWidth
                  className="h-16"
                >
                  {sellState === 'selling' ? (
                    'Selling…'
                  ) : (
                    <>
                      Accept Buyback · {prefix}
                      <RollingNumber
                        value={offer.grossPrice}
                        start={ctaRoll}
                        instant={instant}
                      />
                      {suffix}
                    </>
                  )}
                </Button>
              </RevealStage>
            </motion.div>

            {showCreateShipping && (
              <motion.div variants={offerChild} className="self-center">
                <RevealStage
                  visible={visibleShipping}
                  instant={instant}
                  className="self-center"
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onCreateShipping}
                    className="mx-auto !h-auto !px-0 underline underline-offset-4 type-subhead font-normal"
                  >
                    Redeem This Item
                  </Button>
                </RevealStage>
              </motion.div>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="resolved"
            variants={resolvedContainer}
            initial="hidden"
            animate="show"
            className="flex flex-col items-center gap-3 w-full"
          >
            <motion.div variants={resolvedChild}>
              <BuybackReceivedLine amount={offer.grossPrice} />
            </motion.div>

            <motion.div variants={resolvedChild} className="w-full">
              <div className="relative w-full">
                <BuybackRippleWaves />
                <Button
                  variant="primary"
                  size="xl"
                  onClick={onOpenNextPack}
                  fullWidth
                  // No gap of its own: the bolt's slot carries the gap and
                  // animates it, so the label glides as the bolt folds
                  className="relative z-10 h-16 gap-0"
                  leftIcon={<SkipBolt />}
                >
                  {nextPackCta}
                </Button>
              </div>
            </motion.div>

            {/* Invisible placeholder matching the offer phase's Create
                Shipping row, so the primary CTA lands at the SAME vertical
                spot Accept Buyback occupied — no jump on the swap. */}
            {showCreateShipping && (
              <div
                aria-hidden
                className="invisible self-center pointer-events-none"
              >
                <Button
                  variant="ghost"
                  size="sm"
                  className="mx-auto !h-auto !px-0 underline underline-offset-4 type-subhead font-normal"
                >
                  Create Shipping
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
